import { MouseEvent } from 'react';
import { Avatar, Tooltip } from '@material-ui/core';
import { IconStar, IconExternalLink, IconQuestionMark } from '@tabler/icons';
import { formatDistanceStrict } from 'date-fns';
import { es } from 'date-fns/locale';

import { useDialog } from '#root/hooks';
import { OrderPriceDialog, ProvinceDialog } from '#root/components';
import { GuveryShippingBadge } from '#root/ui-components';
import { PersonIcon, InboxIcon } from '#root/icons';
import { ImageHorizontal } from '#root/assets';
import * as VARIABLES from '#root/constants/variables';
import { Badge, Button, Card, PricePair } from '#root/components/ui';
import styles from './orderCard.module.scss';

const getTime = (orderDate) => {
  let time;
  try {
    time = orderDate
      ? formatDistanceStrict(new Date(orderDate.replace(/ /g, 'T')), new Date(), { locale: es })
      : '---';
  } catch (error) {
    time = 'error';
  }
  return time;
};

interface OrderCardProps {
  xref?: any;
  order: any;
  disableOffer?: boolean;
  onActionClick: (e?: MouseEvent<HTMLElement>) => void;
}

const OrderCard = (props: OrderCardProps) => {
  const { xref, order, onActionClick, disableOffer } = props;
  const provinceDialog = useDialog();
  const priceDialog = useDialog();

  const orderDate = order?.fecpub ? order?.fecpub : order?.feccre;
  const formatDate = getTime(orderDate);

  return (
    <Card noHover className={styles.root}>
      <div ref={xref} className={styles.cover}>
        <a
          className="guv-no-decoration"
          href={order.url}
          target="_blank"
          rel="noopener noreferrer">
          {order?.img_path ? (
            <img src={order.img_path} alt="Pedido" />
          ) : (
            <img className={styles.placeholder} src={ImageHorizontal} alt="Pedido" />
          )}
        </a>
      </div>
      <div className={styles.content}>
        <div className={styles.contentWrap}>

          <div className={styles.orderInfo}>
            <div className={styles.header}>
              <span className={styles.date}>Hace {formatDate}</span>
              {order.flag_envio === 1 && (
                <Badge
                  variant="shipping"
                  size="sm"
                  onClick={provinceDialog.openDialog}
                  icon={<IconQuestionMark size={14} />}>
                  Envío Nacional
                </Badge>
              )}
            </div>

            <a
              className="guv-no-decoration guv-block"
              href={order?.url}
              target="_blank"
              rel="noopener noreferrer">
              <h3 className={styles.title}>{order?.tit}</h3>
            </a>

            <div className={styles.badges}>
              <a
                className={styles.badge}
                href={order?.url}
                target="_blank"
                rel="noopener noreferrer">
                {order?.url_host}
              </a>
              {order?.can && order?.can > 1 && (
                <span className={styles.badge}>
                  <strong>x {order?.can}</strong>
                </span>
              )}
              {order?.condicion && (
                <span className={styles.badge}>{order?.condicion}</span>
              )}
              {order?.empaque && <span className={styles.badge}>{order?.empaque}</span>}
            </div>

            {order?.obs && <p className={styles.obs}>{order.obs}</p>}
          </div>
          {order?.user && (
            <div className={styles.userInfo}>
              <div className={styles.avatar}>
                <Avatar
                  alt={order.user?.mask_fullname}
                  src={VARIABLES.IMAGE_PROFILE_PATH + order.user?.pic + '.png'}
                  sizes="24">
                  <PersonIcon />
                </Avatar>
                <div>
                  <span className={styles.userName}>{order.user?.mask_fullname}</span>
                  {order?.user?.calcom && (
                    <Tooltip arrow title="Puntaje del comprador" placement="bottom">
                      <div className={styles.userScore}>
                        <IconStar />
                        <span>{order.user.calcom}</span>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        <div className={styles.actionsWrap}>
          {/* Etiquetas cortas: los nombres completos van en el diálogo que abre el sub-card */}
          <PricePair
            block
            className={styles.pricePair}
            priceLabel="Precio ref."
            price={order?.pre}
            commissionLabel="Comisión prop."
            commission={order?.com}
            aria-label="Ver qué son el precio referencial y la comisión propuesta"
            onClick={priceDialog.openDialog}
          />
          <Button
            className={styles.offerButton}
            disabled={disableOffer}
            variant="primary"
            icon={<InboxIcon />}
            onClick={() => onActionClick(order)}>
            Hacer Oferta
          </Button>
        </div>
      </div>
      <ProvinceDialog open={provinceDialog.open} onClose={provinceDialog.closeDialog} />
      <OrderPriceDialog open={priceDialog.open} onClose={priceDialog.closeDialog} order={order} />
    </Card>
  );
};

export default OrderCard;
